import { useEffect } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { setAemLanguageCode } from './lang/aemLanguageConfiguration';
import { createLocaleShort, getSupportedLanguageKey } from './lang/languageConfiguration';
import { setLocaleShort } from './lang/languageData';
import { MarketplaceContent } from './MarketplaceContent';
import 'cssuseragent';

import { useLocation } from 'react-router';

import { getLocale } from '../../configuration';
import { sendError } from '../../configuration/lang/services';
import type { RootState } from '../../configuration/setup/store';
import { getIsAccountNotFound } from './common/billing/redux/billing.redux';
import { AccountNotFoundState } from './common/error/AccountNotFoundState';
import { MARKETPLACE_PRODUCT_DETAILS_PATH } from './common/routes';
import { safeDataLayerPush } from './common/utils/googleTagManagerWrapper';

interface Props {
    isAccountNotFound: boolean;
    userLanguage: string;
}

function setDocumentTitle(pathname: string, intl: IntlShape) {
    if (pathname.includes('checkout')) {
        document.title = 'RIO Checkout';
    } else {
        const customerCenterTitleKey = 'marketplace.customerCenter';
        if (intl.messages[customerCenterTitleKey]) {
            document.title = `RIO ${intl.formatMessage({ id: customerCenterTitleKey })}`;
        } else {
            document.title = 'RIO Customer Center';
            // Only send error if user is not immediately redirected
            if (!pathname.startsWith(`/${MARKETPLACE_PRODUCT_DETAILS_PATH}/`)) {
                sendError(new Error('Missing translations for document title'));
            }
        }
    }
}

function Marketplace(props: Props) {
    const { userLanguage, isAccountNotFound } = props;
    const { pathname } = useLocation();
    useEffect(
        () =>
            safeDataLayerPush(() => ({
                event: 'virtPath',
                virtPath: pathname,
            })),
        [pathname]
    );
    const intl = useIntl();
    setDocumentTitle(pathname, intl);

    const key = getSupportedLanguageKey(userLanguage);
    setLocaleShort(createLocaleShort(key));
    setAemLanguageCode(key);

    if (isAccountNotFound) {
        return <AccountNotFoundState />;
    }
    return <MarketplaceContent />;
}

export function mapStateToProps(state: RootState) {
    return {
        userLanguage: getLocale(state),
        isAccountNotFound: getIsAccountNotFound(state),
    };
}

export const MarketplaceContainer = connect(mapStateToProps)(Marketplace);
